<template>
  <div class="modern-dashboard">
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div class="header-content">
        <div>
          <h4 class="dashboard-title">Dashboard Overview</h4>
          <p class="dashboard-subtitle">Real-time insights into app usage and parental controls</p>
        </div>
        <div class="date-filter">
          <select class="filter-select" v-model="selectedPeriod">
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Last 90 Days</option>
            <option>All Time</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card" data-aos="fade-up">
        <div class="stat-icon stat-icon-primary">
          <i class="fas fa-mobile-alt"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-value">{{ stats.activeDevices }}</h3>
          <p class="stat-label">Active Devices</p>
          <!--<span class="stat-change positive">
            <i class="fas fa-arrow-up"></i> 12% from last month
          </span>-->
        </div>
      </div>

      <div class="stat-card" data-aos="fade-up" data-aos-delay="100">
        <div class="stat-icon stat-icon-success">
          <i class="fas fa-download"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-value">{{ stats.totalInstalls }}</h3>
          <p class="stat-label">Total App Installs</p>
          <!--<span class="stat-change positive">
            <i class="fas fa-arrow-up"></i> 8.5% from last month
          </span>-->
        </div>
      </div>

      <div class="stat-card" data-aos="fade-up" data-aos-delay="200">
        <div class="stat-icon stat-icon-warning">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <h3 class="stat-value">{{ stats.avgScreenTime }}</h3>
          <p class="stat-label">Avg Screen Time</p>
          <!--<span class="stat-change negative">
            <i class="fas fa-arrow-down"></i> 3.2% from last month
          </span>-->
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="content-grid">
      
      <!-- Peak Usage Hours Chart -->
      <!--<div class="dashboard-card full-width" data-aos="fade-up">
        <div class="card-header">
          <div>
            <h5 class="card-title">
              <i class="fas fa-chart-line"></i> Peak Usage Hours
            </h5>
            <p class="card-subtitle">Device usage by hour of the day ({{ selectedPeriod }})</p>
          </div>
        </div>
        <div class="card-body">
          <div class="chart-container">
            <canvas id="peakHoursChart"></canvas>
          </div>
        </div>
      </div>-->

      <!-- Top Installed Apps -->
      <div class="dashboard-card" data-aos="fade-up" data-aos-delay="100">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-download"></i> Top 10 Installed Apps
          </h5>
          <a href="#" class="view-all-link">View All →</a>
        </div>
        <div class="card-body">
          <div class="app-list">
            <div v-for="(app, index) in topInstalledApps" :key="'installed-' + index" class="app-item">
              <div class="app-rank rank-installed">{{ index + 1 }}</div>
              <img :src="app.icon" :alt="app.name" class="app-icon" @error="handleImageError" />
              <div class="app-info">
                <h6 class="app-name">{{ app.name }}</h6>
                <p class="app-category">{{ app.category }}</p>
              </div>
              <div class="app-stat">
                <span class="stat-number">{{ app.count }}</span>
                <span class="stat-label">installs</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Requested Apps -->
      <div class="dashboard-card" data-aos="fade-up" data-aos-delay="200">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-hand-paper"></i> Top 10 Requested Apps
          </h5>
          <a href="#" class="view-all-link">View All →</a>
        </div>
        <div class="card-body">
          <div class="app-list">
            <div v-for="(app, index) in topRequestedApps" :key="'requested-' + index" class="app-item">
              <div class="app-rank rank-requested">{{ index + 1 }}</div>
              <img :src="app.icon" :alt="app.name" class="app-icon" @error="handleImageError" />
              <div class="app-info">
                <h6 class="app-name">{{ app.name }}</h6>
                <p class="app-category">{{ app.category }}</p>
              </div>
              <div class="app-stat">
                <span class="stat-number">{{ app.count }}</span>
                <span class="stat-label">requests</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Used Apps -->
      <div class="dashboard-card" data-aos="fade-up" data-aos-delay="100">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-fire"></i> Most Used Apps
          </h5>
          <a href="#" class="view-all-link">View All →</a>
        </div>
        <div class="card-body">
          <div class="app-list">
            <div v-for="(app, index) in topUsedApps" :key="'used-' + index" class="app-item">
              <div class="app-rank rank-used">{{ index + 1 }}</div>
              <img :src="app.appIcon" :alt="app.appTitle" class="app-icon" @error="handleImageError" />
              <div class="app-info">
                <h6 class="app-name">{{ app.appTitle || app.packageName }}</h6>
                <p class="app-category">{{ app.playStoreCategory }}</p>
              </div>
              <div class="app-stat">
                <span class="stat-number">{{ app.totalTimeInHours }}H</span>
                <span class="stat-label">avg usage</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Rated by Parents -->
      <div class="dashboard-card" data-aos="fade-up" data-aos-delay="200">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-user-shield"></i> Top Rated by Parents
          </h5>
          <a href="#" class="view-all-link">View All →</a>
        </div>
        <div class="card-body">
          <div class="app-list">
            <div v-for="(app, index) in topRatedParents" :key="'parent-' + index" class="app-item">
              <div class="app-rank rank-parent">{{ index + 1 }}</div>
              <img :src="app.icon" :alt="app.name" class="app-icon" @error="handleImageError" />
              <div class="app-info">
                <h6 class="app-name">{{ app.name }}</h6>
                <div class="rating-stars">
                  <i v-for="star in 5" :key="star" class="fas fa-star" :class="{ filled: star <= app.rating }"></i>
                </div>
              </div>
              <div class="app-stat">
                <span class="stat-number">{{ app.rating }}</span>
                <span class="stat-label">{{ app.reviews }} reviews</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Rated by Kids -->
      <div class="dashboard-card" data-aos="fade-up" data-aos-delay="300">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-child"></i> Top Rated by Kids
          </h5>
          <a href="#" class="view-all-link">View All →</a>
        </div>
        <div class="card-body">
          <div class="app-list">
            <div v-for="(app, index) in topRatedKids" :key="'kid-' + index" class="app-item">
              <div class="app-rank rank-kid">{{ index + 1 }}</div>
              <img :src="app.icon" :alt="app.name" class="app-icon" @error="handleImageError" />
              <div class="app-info">
                <h6 class="app-name">{{ app.name }}</h6>
                <div class="rating-stars">
                  <i v-for="star in 5" :key="star" class="fas fa-star" :class="{ filled: star <= app.rating }"></i>
                </div>
              </div>
              <div class="app-stat">
                <span class="stat-number">{{ app.rating }}</span>
                <span class="stat-label">{{ app.reviews }} reviews</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Category Distribution -->
      <!--<div class="dashboard-card" data-aos="fade-up" data-aos-delay="100">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-chart-pie"></i> App Categories
          </h5>
        </div>
        <div class="card-body">
          <div class="chart-container small-chart">
            <canvas id="categoryChart"></canvas>
          </div>
        </div>
      </div>-->

      <!-- Quick Stats -->
      <div class="dashboard-card" data-aos="fade-up" data-aos-delay="200">
        <div class="card-header">
          <h5 class="card-title">
            <i class="fas fa-bolt"></i> Quick Stats
          </h5>
        </div>
        <div class="card-body">
          <div class="quick-stats">
            <div class="quick-stat-item">
              <div class="quick-stat-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="quick-stat-content">
                <h4>{{ quickStats.approved }}</h4>
                <p>Approved Requests</p>
              </div>
            </div>
            <div class="quick-stat-item">
              <div class="quick-stat-icon pending">
                <i class="fas fa-hourglass-half"></i>
              </div>
              <div class="quick-stat-content">
                <h4>{{ quickStats.pending }}</h4>
                <p>Pending Requests</p>
              </div>
            </div>
            <div class="quick-stat-item">
              <div class="quick-stat-icon denied">
                <i class="fas fa-times-circle"></i>
              </div>
              <div class="quick-stat-content">
                <h4>{{ quickStats.denied }}</h4>
                <p>Denied Requests</p>
              </div>
            </div>
            <div class="quick-stat-item">
              <div class="quick-stat-icon active">
                <i class="fas fa-shield-alt"></i>
              </div>
              <div class="quick-stat-content">
                <h4>{{ quickStats.restrictions }}</h4>
                <p>Active Restrictions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardService from "../../services/SuperAdmin/Kids/DashboardService";
export default {
  data() {
    return {
      selectedPeriod: 'All Time',
      stats: {
        activeDevices: '1,247',
        totalInstalls: '8,942',
        avgScreenTime: '156H',
        avgRating: '4.6'
      },
      quickStats: {
        approved: '892',
        pending: '156',
        denied: '234',
        restrictions: '567'
      },
      topInstalledApps: [],
      topRequestedApps: [],
      topUsedApps: [
      ],
      topRatedParents: [
        { name: 'Khan Academy Kids', rating: 4.9, reviews: '1,234', icon: 'https://cdn-icons-png.flaticon.com/512/2991/2991148.png' },
        { name: 'ABCmouse', rating: 4.8, reviews: '987', icon: 'https://cdn-icons-png.flaticon.com/512/2912/2912163.png' },
        { name: 'PBS Kids', rating: 4.8, reviews: '856', icon: 'https://cdn-icons-png.flaticon.com/512/2504/2504929.png' },
        { name: 'Duolingo Kids', rating: 4.7, reviews: '745', icon: 'https://cdn-icons-png.flaticon.com/512/5968/5968872.png' },
        { name: 'Chess for Kids', rating: 4.7, reviews: '623', icon: 'https://cdn-icons-png.flaticon.com/512/8488/8488933.png' },
        { name: 'Starfall', rating: 4.6, reviews: '589', icon: 'https://cdn-icons-png.flaticon.com/512/2912/2912163.png' },
        { name: 'Epic! Books', rating: 4.6, reviews: '534', icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330294.png' },
        { name: 'Osmo', rating: 4.5, reviews: '478', icon: 'https://cdn-icons-png.flaticon.com/512/2991/2991148.png' },
        { name: 'Scratch Jr', rating: 4.5, reviews: '423', icon: 'https://cdn-icons-png.flaticon.com/512/2620/2620521.png' },
        { name: 'Kodable', rating: 4.4, reviews: '389', icon: 'https://cdn-icons-png.flaticon.com/512/2620/2620521.png' }
      ],
      topRatedKids: [
        { name: 'Minecraft', rating: 4.9, reviews: '2,341', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969294.png' },
        { name: 'Roblox', rating: 4.8, reviews: '2,156', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969346.png' },
        { name: 'Among Us', rating: 4.7, reviews: '1,987', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969346.png' },
        { name: 'YouTube Kids', rating: 4.7, reviews: '1,823', icon: 'https://cdn-icons-png.flaticon.com/512/1384/1384060.png' },
        { name: 'TikTok', rating: 4.6, reviews: '1,654', icon: 'https://cdn-icons-png.flaticon.com/512/3046/3046121.png' },
        { name: 'Brawl Stars', rating: 4.6, reviews: '1,534', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969294.png' },
        { name: 'Fortnite', rating: 4.5, reviews: '1,423', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969294.png' },
        { name: 'Toca Life', rating: 4.5, reviews: '1,298', icon: 'https://cdn-icons-png.flaticon.com/512/3595/3595455.png' },
        { name: 'Subway Surfers', rating: 4.4, reviews: '1,167', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969294.png' },
        { name: 'Candy Crush', rating: 4.4, reviews: '1,089', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969294.png' }
      ]
    }
  },
  mounted() {
    this.initCharts();
  },
  methods: {
    initCharts() {
      // Peak Hours Chart
      const peakCtx = document.getElementById('peakHoursChart');
      if (peakCtx) {
        new Chart(peakCtx, {
          type: 'line',
          data: {
            labels: ['12 AM', '2 AM', '4 AM', '6 AM', '8 AM', '10 AM', '12 PM', '2 PM', '4 PM', '6 PM', '8 PM', '10 PM'],
            datasets: [{
              label: 'Active Users',
              data: [120, 85, 45, 78, 245, 389, 467, 512, 678, 845, 923, 567],
              borderColor: 'rgb(99, 102, 241)',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              tension: 0.4,
              fill: true,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: 'rgb(99, 102, 241)',
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                cornerRadius: 8
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              x: {
                grid: { display: false }
              }
            }
          }
        });
      }

      // Category Chart
      const catCtx = document.getElementById('categoryChart');
      if (catCtx) {
        new Chart(catCtx, {
          type: 'doughnut',
          data: {
            labels: ['Education', 'Games', 'Entertainment', 'Social', 'Creative'],
            datasets: [{
              data: [35, 28, 18, 12, 7],
              backgroundColor: [
                'rgb(99, 102, 241)',
                'rgb(16, 185, 129)',
                'rgb(245, 158, 11)',
                'rgb(239, 68, 68)',
                'rgb(139, 92, 246)'
              ],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  padding: 15,
                  usePointStyle: true
                }
              }
            }
          }
        });
      }
    },
    handleImageError(event) {
      event.target.src = 'https://via.placeholder.com/48x48/9CA3AF/FFFFFF?text=📱';
    },
    getWingioDashboardDetail() {
      let data = { selectedPeriod: this.selectedPeriod};
        DashboardService.getWingioDashboardDetail(data)
          .then((response) => {
            this.stats.activeDevices = response.data.data.activeDevices
            this.stats.totalInstalls = response.data.data.totalAppsInstalled
            this.stats.avgScreenTime = response.data.data.avgScreenTimeInHours
            this.topUsedApps = response.data.data.topUsedApps
            this.topInstalledApps = response.data.data.topInstalledApps
            this.topRequestedApps = response.data.data.topRequestedApps
            this.$toast.success(response.data.respones_msg);
          }).catch((e) => {
            this.$toast.error(e.response.data.respones_msg);
          });
    }
  },
  beforeMount() {
    this.getWingioDashboardDetail();
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');

.modern-dashboard {
  font-family: 'Inter', sans-serif;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 2rem;
}

/* Header */
.dashboard-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2.5rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.3);
  backdrop-filter: blur(10px);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.dashboard-title {
  color: white;
  font-size: 2.2rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
}

.dashboard-subtitle {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-size: 1.1rem;
  font-weight: 400;
}

.date-filter {
  display: flex;
  gap: 1rem;
}

.filter-select {
  padding: 12px 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.filter-select:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.filter-select option {
  color: #333;
  background: white;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.stat-card {
  background: rgba(255, 255, 255, 0.95);
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  width: 70px;
  height: 70px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.stat-card:hover .stat-icon {
  transform: scale(1.1);
}

.stat-icon-primary { background: linear-gradient(135deg, #667eea, #764ba2); color: white; }
.stat-icon-success { background: linear-gradient(135deg, #10b981, #059669); color: white; }
.stat-icon-warning { background: linear-gradient(135deg, #f59e0b, #d97706); color: white; }
.stat-icon-info { background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; }

.stat-value {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1;
}

.stat-label {
  color: #64748b;
  font-size: 1rem;
  margin: 0.5rem 0 0.25rem 0;
  font-weight: 500;
}

.stat-change {
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stat-change.positive { color: #10b981; }
.stat-change.negative { color: #ef4444; }

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
  gap: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.full-width { grid-column: 1 / -1; }

/* Dashboard Cards */
.dashboard-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.4s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.dashboard-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}

.card-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-subtitle {
  margin: 0.25rem 0 0 0;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 400;
}

.view-all-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.view-all-link:hover {
  color: #764ba2;
}

.card-body {
  padding: 2rem;
}

.chart-container {
  height: 300px;
  position: relative;
}

.small-chart {
  height: 250px !important;
}

/* App List */
.app-list {
  max-height: 400px;
  overflow-y: auto;
}

.app-item {
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.app-item:hover {
  background: rgba(102, 126, 234, 0.05);
  padding-left: 1rem;
  border-radius: 12px;
  margin: 0 -1rem;
}

.app-item:last-child {
  border-bottom: none;
}

.app-rank {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.app-rank.rank-installed { background: linear-gradient(135deg, #0b28f5, #061ed9); }
.app-rank.rank-requested { background: linear-gradient(135deg, #f59e0b, #d97706); }
.app-rank.rank-used { background: linear-gradient(135deg, #ef4444, #dc2626); }
.app-rank.rank-parent { background: linear-gradient(135deg, #10b981, #059669); }
.app-rank.rank-kid { background: linear-gradient(135deg, #3b82f6, #2563eb); }

.app-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  margin-right: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.app-info {
  flex: 1;
  min-width: 0;
}

.app-name {
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-category {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
}

.app-stat {
  text-align: right;
  flex-shrink: 0;
}

.stat-number {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #9ca3af;
  font-weight: 500;
}

/* Rating Stars */
.rating-stars {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.rating-stars i {
  font-size: 0.9rem;
  color: #d1d5db;
}

.rating-stars i.filled {
  color: #fbbf24;
}

/* Quick Stats */
.quick-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.quick-stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.quick-stat-item:hover {
  transform: translateX(5px);
}

.quick-stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.quick-stat-icon { background: #e8f5e9; }
.quick-stat-icon.pending { background: #fff3e0; }
.quick-stat-icon.denied { background: #ffebee; }
.quick-stat-icon.active { background: #e3f2fd; }

.quick-stat-content h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

.quick-stat-content p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-dashboard {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
}
</style>
